/**
 * The agent sandbox's browser host in the app: this tab runs the sandbox calls a paused run lists
 * for the workspaces it holds (sandbox/host/sandboxHost.ts), and as the elected host the user's
 * calls from any thread or surface (`sandbox.waiting`); another elected tab keeps it standing by
 * (policy.ts), and losing the role hands its workspaces over (followHostRole), as does a sandbox that
 * would not start in this tab (markBootFailed). Mounted only where
 * the package sources are configured; elsewhere waiting sandbox calls go unanswered until their
 * deadline. The executor (its frame, worker and toolchain) loads
 * on a workspace's first call, never with the app. A repl cell's tool calls go to the server over
 * the agent socket (sandbox/host/toolCallBridge.ts, CM3).
 */
import { agentClient, TAB_ID } from "./agentClient";
import type { AgentState } from "./store/agentSlice";
import type { PackageSources } from "./sandbox/core/types";
import { sandboxPackages } from "./sandboxPackages";
import { getPanelFileBytes, getPanelFiles } from "./sandbox/client/api";
import { onBootFailed } from "./sandbox/host/candidate";
import { recordCellsWhile } from "./sandbox/host/cellRecords";
import { electedPolicy, standsBy } from "./sandbox/host/policy";
import {
  CLAIM_MS,
  claimForPreview,
  createSandboxHost,
  followHostRole,
  followWaitingCalls,
  followWorkspaceChanges,
  type SandboxHost,
} from "./sandbox/host/sandboxHost";
import { createToolCallBridge } from "./sandbox/host/toolCallBridge";
import { createSyncEngine } from "./sandbox/sync/engine";
import { workerPrepare } from "./sandbox/sync/prepare";

export { DEV_PACKAGES_KEY, sandboxPackages } from "./sandboxPackages";

type Mounted = { host: SandboxHost; packages: PackageSources; store: Store };

/** This tab's running host, while mounted. */
let current: Mounted | null = null;
/** The store the host follows, while mounted (also while the page sits in the back-forward cache). */
let followed: Store | null = null;
/** Claims waiting for the host a restored page mounts. */
const mounts = new Set<() => void>();

const windowTimers = {
  setTimeout: (callback: () => void, ms: number) => window.setTimeout(callback, ms),
  clearTimeout: (handle: unknown) => window.clearTimeout(handle as number | undefined),
};

/**
 * Read-only access to a workspace this tab runs (a preview's reads), or null when another tab or
 * host runs it, or none does.
 */
export function workspaceReader(workspaceId: string) {
  return current?.host.reader(workspaceId) ?? null;
}

/**
 * A preview's reader for a workspace no tab may hold yet: this tab takes it up (its lease, then its
 * executor) unless another tab or a hosted executor is the host; null then, with no host here, or
 * on `signal`'s abort. A claim the page's leaving cut (pagehide) claims again on the host the
 * restored page mounts.
 *
 * That re-claim reads the role the store kept from before the back-forward cache, not the one the
 * server gives the restored page's hello, so it may try for a lease another tab was elected to
 * meanwhile. The window is accepted: it lasts until that frame lands, electedPolicy re-reads the
 * role after acquiring (and gives back a lease it should not hold), and followHostRole hands the
 * workspace over once the frame says standby. Waiting for the frame instead would need a frame
 * signal from the socket: the store drops a same-epoch re-announcement (newerHost), and the server
 * sends none when its election fails, so waiting on the store could hang until CLAIM_MS.
 */
export async function claimWorkspace(workspaceId: string, signal?: AbortSignal) {
  let mounted = current;
  while (mounted && !signal?.aborted) {
    const { host, store } = mounted;
    const role = {
      get: () => store.getState().agent.sandboxHost ?? null,
      subscribe: (listener: () => void) => store.subscribe(listener),
    };
    const reader = await claimForPreview(host, role, workspaceId, windowTimers, CLAIM_MS, signal);
    if (reader || !host.isDisposed()) return reader;
    mounted = await nextMount(mounted, signal);
  }
  return null;
}

/**
 * Calls `listener` each time this tab's part turns from standing by, or from no frame yet, to the
 * host (a preview shown as another tab's claims again then); returns the unsubscribe. After a
 * reload the server keeps the page before it as the host for HOLDER_GONE_SECONDS, so the only tab
 * stands by first. A tab already the host never fires: a claim that failed there stays failed.
 */
export function onHosting(listener: () => void): () => void {
  const store = followed;
  if (!store) return () => undefined;
  const away = () => {
    const part = store.getState().agent.sandboxHost ?? null;
    return part === null || standsBy(part);
  };
  let was = away();
  return store.subscribe(() => {
    const now = away();
    if (was && !now) listener();
    was = now;
  });
}

/** How many claims wait for the restored page's host (tests). */
export function claimsWaitingForHost(): number {
  return mounts.size;
}

/** The host mounted after `gone`, once there is one; null on `signal`'s abort or after CLAIM_MS. */
function nextMount(gone: Mounted, signal?: AbortSignal): Promise<Mounted | null> {
  if (signal?.aborted) return Promise.resolve(null);
  if (current && current !== gone) return Promise.resolve(current);
  return new Promise((resolve) => {
    const done = (mounted: Mounted | null) => {
      mounts.delete(onMount);
      signal?.removeEventListener("abort", onAbort);
      windowTimers.clearTimeout(timer);
      resolve(mounted);
    };
    const onMount = () => done(current);
    const onAbort = () => done(null);
    mounts.add(onMount);
    signal?.addEventListener("abort", onAbort);
    const timer = windowTimers.setTimeout(() => done(null), CLAIM_MS);
  });
}

/** The package sources of the running host (a preview's script mirror), or null. */
export function runningPackages(): PackageSources | null {
  return current?.packages ?? null;
}

type Store = {
  getState(): { agent: AgentState; auth?: { user: { sandbox_dev_inspector?: boolean } | null } };
  subscribe(listener: () => void): () => void;
};

/**
 * Starts this tab's sandbox host when the packages are configured; returns its unmount. The host
 * goes with the page (pagehide gives its leases up) and comes back when the page is restored from
 * the back-forward cache.
 */
export function mountSandboxHost(
  store: Store,
  packages: PackageSources | null = sandboxPackages()
): () => void {
  if (!packages) return () => undefined;
  // The dev inspector's client column: only while the server's flag is on.
  const unrecord = recordCellsWhile(
    () => store.getState().auth?.user?.sandbox_dev_inspector === true
  );
  let stop: (() => void) | null = startHost(store, packages);
  followed = store;
  const leave = () => {
    stop?.();
    stop = null;
  };
  const back = (event: PageTransitionEvent) => {
    if (event.persisted && !stop) stop = startHost(store, packages);
  };
  window.addEventListener("pagehide", leave);
  window.addEventListener("pageshow", back);
  return () => {
    window.removeEventListener("pagehide", leave);
    window.removeEventListener("pageshow", back);
    leave();
    if (followed === store) followed = null;
    unrecord();
  };
}

function startHost(store: Store, packages: PackageSources): () => void {
  const timers = {
    now: () => Date.now(),
    setTimeout: (callback: () => void, ms: number) => window.setTimeout(callback, ms),
    clearTimeout: (handle: unknown) => window.clearTimeout(handle as number | undefined),
  };
  const prepare = workerPrepare();
  const bridge = createToolCallBridge({ socket: agentClient, timers });
  const host = createSandboxHost({
    owner: TAB_ID,
    policy: electedPolicy(() => store.getState().agent.sandboxHost ?? null),
    async startExecutor(workspaceId, serving) {
      const { browserExecutor } = await import("./sandbox/host/browserExecutor");
      return browserExecutor({ packages, hostCall: bridge.handler(serving) })(workspaceId);
    },
    handIn: (answer) => agentClient.handInSandbox(answer),
    sync: (hooks) => createSyncEngine({ ...hooks, timers, prepare }),
    files: {
      list: async (workspaceId) => (await getPanelFiles(workspaceId)).files,
      read: getPanelFileBytes,
    },
    timers,
  });
  const unfollow = followWaitingCalls(store, host);
  const unwatch = followWorkspaceChanges(agentClient, host);
  const mounted = { host, packages, store };
  current = mounted;
  for (const onMount of [...mounts]) onMount();
  const unfollowRole = followHostRole(store, host);
  // The mark changes nothing in the store: the leases this tab holds go to a healthy tab now.
  const unfollowBoot = onBootFailed(() => void host.handOver());
  return () => {
    unfollowBoot();
    unfollowRole();
    unwatch();
    if (current === mounted) current = null;
    unfollow();
    void host.dispose();
    bridge.dispose();
  };
}
